import React, { useState } from 'react'
import moment from 'moment'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { formatDate } from '../../../i18n/format'
import PaymentConfirmModal from '../../../containers/modal/payment-confirm-modal'
import ExpressCheckoutRow from '../../../containers/express-checkout/ExpressCheckoutRow'
import { formatMoney } from '../../../utils/currency'
import type { PromoTimeLeft } from '../../../utils/dragonBoatPromo'
import PromoCountdown from './PromoCountdown'

/**
 * One live campaign on one plan, described by whichever campaign module is in its window. The
 * card renders the treatment (discount note, live clock) without knowing which campaign it is.
 * `getTimeLeft` must be a module-level function: PromoCountdown rebuilds its interval whenever
 * the reference changes.
 */
export type PlanPromo = {
  discountPercent?: number
  durationMonths?: number
  getTimeLeft?: () => PromoTimeLeft
}

interface PlanOptionProps {
  plan: any
  /** The plan we steer people to wears the green frame and the filled button. */
  featured?: boolean
  tagline: string
  promo?: PlanPromo
  /** The learner's own plan (membership.name), so a CTA can say "Switch to yearly". */
  currentPlanValue?: string
  /** A checkout is opening (any card): every CTA waits, the one in flight says so. */
  pendingPriceId: string | null
  error?: string
  onUpgrade: (plan: any) => void
  onDowngrade: (plan: any) => Promise<void>
  onWalletStart?: (plan: any) => void
  /** Weekly on the monthly tab: annual-only, so the card points at the yearly tab instead. */
  locked?: { note: string; actionLabel: string; onUnlock: () => void }
}

/** "Pro Plan" / "maayot Pro" -> "Pro". */
export const shortPlanName = (name?: string) =>
  String(name || '')
    .replace(/^maayot\s+/i, '')
    .replace(/\s+Plan$/i, '')

// getButtonText says "You're On …" for the learner's own plan even where the price id doesn't
// match (Weekly, Free), so either signal means there is nothing to buy on this card.
const isCurrent = (plan: any) => Boolean(plan.isCurrentPlan) || /^You're On/.test(plan.buttonText || '')

const ctaLabel = (t: TFunction, plan: any, promo: PlanPromo | undefined, currentPlanValue: string | undefined) => {
  const name = shortPlanName(plan.name)
  const isUpgrade = plan.buttonText === 'Upgrade'
  const isDowngrade = plan.buttonText === 'Downgrade'
  const sameTier = plan.value === currentPlanValue
  if (isCurrent(plan)) return t('card.currentPlan')
  if (plan.trialEligible && isUpgrade) return t('card.tryFree', { plan: name, days: plan.trialDays })
  // Display-only override (the drip trial-offer flow); buttonText keeps driving behaviour.
  if (plan.buttonLabel && isUpgrade) return plan.buttonLabel
  if (promo?.discountPercent && isUpgrade) return t('card.claimDiscount', { percent: promo.discountPercent, plan: name })
  if (isUpgrade) return sameTier ? t('card.switchToYearly') : t('card.upgradeTo', { plan: name })
  if (isDowngrade) return sameTier ? t('card.switchToMonthly') : t('card.switchTo', { plan: name })
  // buttonText is a code ('Upgrade', 'Downgrade', "You're On …"), never shown: an empty label.
  return ''
}

const PlanOption = ({
  plan,
  featured = false,
  tagline,
  promo,
  currentPlanValue,
  pendingPriceId,
  error,
  onUpgrade,
  onDowngrade,
  onWalletStart,
  locked,
}: PlanOptionProps) => {
  const { t } = useTranslation('plan')
  const [confirmDowngrade, setConfirmDowngrade] = useState(false)
  const [isDowngrading, setIsDowngrading] = useState(false)

  const isUpgrade = plan.buttonText === 'Upgrade'
  const isDowngrade = plan.buttonText === 'Downgrade'
  const isPending = pendingPriceId === plan.priceId
  const current = isCurrent(plan)
  // The filled button is for buying. A Pro subscriber's "Switch to monthly" is not the
  // action we lead with, so it gets the outline like every other change.
  const isPrimary = featured && isUpgrade && !current
  const isYearly = plan.periodType === 'yearly' && plan.billingChargeAmount !== undefined
  const showTrial = Boolean(plan.trialEligible && plan.trialDays) && isUpgrade && !current
  const hasOldPrice = plan.originalPrice !== undefined && plan.originalPrice !== plan.price
  const hasOldCharge =
    plan.originalBillingChargeAmount !== undefined && plan.originalBillingChargeAmount !== plan.billingChargeAmount
  // What the first invoice will be once the free days are over: the year up front on yearly,
  // otherwise one month (already the discounted month when a campaign stacks with the trial).
  const firstCharge = isYearly ? plan.billingChargeAmount : plan.price
  const firstChargeDate = formatDate(moment().add(plan.trialDays || 0, 'days').toDate(), 'monthDay')

  const handleClick = () => {
    if (current || pendingPriceId) return
    if (locked) return locked.onUnlock()
    if (isUpgrade) onUpgrade(plan)
    else if (isDowngrade) setConfirmDowngrade(true)
  }

  const handleConfirmDowngrade = async () => {
    setIsDowngrading(true)
    try {
      await onDowngrade(plan)
    } finally {
      setIsDowngrading(false)
      setConfirmDowngrade(false)
    }
  }

  return (
    <article
      className={`plan-option${featured ? ' plan-option--featured' : ''}${locked ? ' plan-option--locked' : ''}`}
      data-plan={shortPlanName(plan.value).toLowerCase()}
      {...(locked
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: locked.onUnlock,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') locked.onUnlock()
            },
          }
        : {})}
    >
      <header className='plan-option-head'>
        <h3 className='plan-option-name'>{shortPlanName(plan.name)}</h3>
        <p className='plan-option-tagline'>{tagline}</p>
      </header>

      <div className='plan-option-pricing'>
        <div className='plan-option-price'>
          {hasOldPrice && <s className='plan-option-amount-old'>{formatMoney(plan.originalPrice, plan.currency)}</s>}
          <span className='plan-option-amount'>{formatMoney(plan.price, plan.currency)}</span>
          <span className='plan-option-per'>{t('card.perMonth')}</span>
        </div>
        <p className='plan-option-billing'>
          {isYearly ? (
            <>
              {hasOldCharge && (
                <s className='plan-option-charge-old'>
                  {formatMoney(plan.originalBillingChargeAmount, plan.currency)}
                </s>
              )}
              <Trans
                t={t}
                i18nKey='card.billedYearly'
                values={{ amount: formatMoney(plan.billingChargeAmount, plan.currency) }}
                components={[<span key='charge' className='plan-option-charge' />]}
              />
            </>
          ) : (
            t('card.billedMonthly')
          )}
        </p>
        {promo?.discountPercent && promo.durationMonths && hasOldPrice && (
          <p className='plan-option-promo'>
            {t('card.promoTerms', {
              percent: promo.discountPercent,
              months: promo.durationMonths,
              price: formatMoney(plan.originalPrice, plan.currency),
            })}
          </p>
        )}
        {promo?.getTimeLeft && hasOldPrice && (
          <PromoCountdown getTimeLeft={promo.getTimeLeft} className='plan-option-countdown' />
        )}
      </div>

      <div className='plan-option-action'>
        {locked ? (
          <p className='plan-option-locked-note'>{locked.note}</p>
        ) : (
          isUpgrade &&
          !current && (
            <ExpressCheckoutRow
              priceId={plan.priceId}
              currency={plan.currency}
              couponCode={plan.appliedCouponCode}
              trialDays={plan.trialDays}
              onConfirmStart={() => onWalletStart?.(plan)}
            />
          )
        )}
        <button
          type='button'
          className={`plan-option-cta${isPrimary ? ' plan-option-cta--primary' : ''}${
            current ? ' plan-option-cta--current' : ''
          }`}
          onClick={(e) => {
            e.stopPropagation()
            handleClick()
          }}
          disabled={current || Boolean(pendingPriceId)}
          aria-busy={isPending || undefined}
        >
          {locked ? locked.actionLabel : isPending ? t('card.openingCheckout') : ctaLabel(t, plan, promo, currentPlanValue)}
        </button>
        {showTrial && (
          <p className='plan-option-trial'>
            <span>{t('card.nothingToday')}</span>{' '}
            <span>{t('card.firstCharge', { amount: formatMoney(firstCharge, plan.currency), date: firstChargeDate })}</span>
          </p>
        )}
        {error && (
          <p className='plan-option-error' role='alert'>
            {error}
          </p>
        )}
      </div>

      <PaymentConfirmModal
        show={confirmDowngrade}
        onHide={() => setConfirmDowngrade(false)}
        onConfirm={handleConfirmDowngrade}
        isDowngrading={isDowngrading}
      />
    </article>
  )
}

export default PlanOption
